import {
  AddOn,
  Appointment,
  CertificateResultOutcome,
  CertificateResultRow,
  MedicalCertificate,
  Service
} from '@/types';
import { getAppointmentServiceIds } from './appointmentServices';

// Drug & Alcohol rows (whether booked as a service or an add-on) are driven by
// the certificate's dedicated Drug Test Result picker, never by the generic
// fit/unfit dropdowns — a screening outcome is negative/non-negative, not a
// fitness verdict. "Not Recorded" (undefined) keeps the row off the
// certificate entirely.
export const isDrugAndAlcoholName = (name: string): boolean => /drug|alcohol/i.test(name);

interface AppointmentCatalogs {
  appointment: Appointment;
  servicesById: Map<string, Service>;
  addOnsById: Map<string, AddOn>;
}

interface BuildCertificateResultsInput extends AppointmentCatalogs {
  primaryOutcome: MedicalCertificate['outcome'];
  drugTestOutcome: MedicalCertificate['drugTestOutcome'];
  extraResults: Record<string, CertificateResultOutcome>;
}

// One row per booked service and add-on test — persisted on the certificate so
// the template can print a result per line. The first non-D&A service mirrors
// the clinical conclusion; other non-D&A rows take their individually picked
// outcome (default fit).
export const buildCertificateResults = ({
  appointment,
  servicesById,
  addOnsById,
  primaryOutcome,
  drugTestOutcome,
  extraResults
}: BuildCertificateResultsInput): CertificateResultRow[] => {
  const rows: CertificateResultRow[] = [];
  let primaryAssigned = false;
  getAppointmentServiceIds(appointment).forEach((serviceId) => {
    const service = servicesById.get(serviceId);
    if (!service) return;
    if (isDrugAndAlcoholName(service.name)) {
      if (drugTestOutcome) {
        rows.push({ kind: 'service', id: serviceId, name: service.name, outcome: drugTestOutcome });
      }
      return;
    }
    const outcome = primaryAssigned
      ? extraResults[`service:${serviceId}`] || 'fit'
      : primaryOutcome || 'fit';
    primaryAssigned = true;
    rows.push({ kind: 'service', id: serviceId, name: service.name, outcome });
  });
  (appointment.addOnIds || []).forEach((addOnId) => {
    const addOn = addOnsById.get(addOnId);
    if (!addOn) return;
    if (isDrugAndAlcoholName(addOn.name)) {
      if (drugTestOutcome) {
        rows.push({ kind: 'add_on', id: addOnId, name: addOn.name, outcome: drugTestOutcome });
      }
      return;
    }
    rows.push({
      kind: 'add_on',
      id: addOnId,
      name: addOn.name,
      outcome: extraResults[`add_on:${addOnId}`] || 'fit'
    });
  });
  return rows;
};

export interface ExtraResultItem {
  key: string;
  name: string;
  kind: 'service' | 'add_on';
}

// The rows the technician sets individually. Excluded: the first non-D&A
// service (mirrors the clinical conclusion) and every D&A service/add-on
// (mirrors the Drug Test Result picker).
export const getExtraResultItems = ({
  appointment,
  servicesById,
  addOnsById
}: AppointmentCatalogs): ExtraResultItem[] => {
  const items: ExtraResultItem[] = [];
  let primarySeen = false;
  getAppointmentServiceIds(appointment).forEach((serviceId) => {
    const service = servicesById.get(serviceId);
    if (!service || isDrugAndAlcoholName(service.name)) return;
    if (!primarySeen) {
      primarySeen = true;
      return;
    }
    items.push({ key: `service:${serviceId}`, name: service.name, kind: 'service' });
  });
  (appointment.addOnIds || []).forEach((addOnId) => {
    const addOn = addOnsById.get(addOnId);
    if (addOn && !isDrugAndAlcoholName(addOn.name)) {
      items.push({ key: `add_on:${addOnId}`, name: addOn.name, kind: 'add_on' });
    }
  });
  return items;
};

export const hasDrugAndAlcoholBooked = ({
  appointment,
  servicesById,
  addOnsById
}: AppointmentCatalogs): boolean =>
  getAppointmentServiceIds(appointment).some((id) => {
    const service = servicesById.get(id);
    return Boolean(service && isDrugAndAlcoholName(service.name));
  }) ||
  (appointment.addOnIds || []).some((id) => {
    const addOn = addOnsById.get(id);
    return Boolean(addOn && isDrugAndAlcoholName(addOn.name));
  });

interface IssueCertificateWarningsInput {
  pendingTemplateNames: string[];
  submissionCount: number;
  drugAndAlcoholBooked: boolean;
  drugTestOutcome: MedicalCertificate['drugTestOutcome'];
}

// Pre-issue sanity checks surfaced to the technician in a single "Issue
// Anyway?" confirmation. None of them block issuing.
export const getIssueCertificateWarnings = ({
  pendingTemplateNames,
  submissionCount,
  drugAndAlcoholBooked,
  drugTestOutcome
}: IssueCertificateWarningsInput): string[] => {
  const warnings: string[] = [];
  if (pendingTemplateNames.length > 0) {
    warnings.push(
      `${pendingTemplateNames.length === 1 ? 'A questionnaire is' : `${pendingTemplateNames.length} questionnaires are`} still outstanding: ${pendingTemplateNames.join(', ')}.`
    );
  } else if (submissionCount === 0) {
    warnings.push('The health questionnaire has not been completed for this appointment.');
  }
  if (drugAndAlcoholBooked && !drugTestOutcome) {
    warnings.push(
      'A Drug & Alcohol test is booked, but the Drug Test Result is "Not Recorded" — no drug screening result will appear on the certificate.'
    );
  }
  return warnings;
};
